// Speech engines for the Elliott voice conversation (Live Simulation step).
//
// The interfaces below are the provider contract: the hook and UI only ever
// talk to ElliottTtsEngine / ElliottSttEngine. v1 ships browser adapters
// (Web Speech API — Chrome/Edge/Safari desktop); the planned server pipeline
// (Deepgram STT → ElevenLabs TTS over a socket) slots in by swapping the
// factories, nothing above this file changes.

/**
 * Why a turn could not be finished by this engine. `remaining` is the part of
 * the turn that was NOT voiced yet, so the next engine in the cascade picks up
 * where this one stopped instead of repeating what the learner already heard.
 * Absent = nothing was voiced; the whole turn is still owed.
 */
export interface ElliottTtsError {
  remaining?: string;
}

export interface ElliottTtsEngine {
  /**
   * Speak a full turn. onStart fires once when audio is actually audible
   * (caption pacing hangs off it); onLevel streams the live speech amplitude
   * 0..1 while audio plays (engines without an audio stream never call it —
   * the avatar falls back to a synthetic cadence); exactly one of
   * onEnd/onError follows, unless cancelled.
   */
  speak: (
    text: string,
    cb: {
      onStart?: () => void;
      onLevel?: (level: number) => void;
      /**
       * Word-synced captions: the current sentence's words plus the index of
       * the word being voiced right now (-1 = no word timing available; show
       * the sentence whole). Server engine estimates boundaries from playback
       * position; browser engine uses real onboundary events.
       */
      onCaption?: (words: string[], activeIndex: number) => void;
      onEnd: () => void;
      onError: (error?: ElliottTtsError) => void;
    }
  ) => void;
  /** Stop any in-flight speech; its callbacks never fire. */
  cancel: () => void;
  /**
   * Hold the turn without ending it: no onEnd, no onError, and no watchdog
   * while held. resume() carries on from where it stopped. The browser voice
   * restarts the interrupted sentence instead, because Chrome cannot reliably
   * resume a paused utterance. Both are no-ops when nothing is playing.
   */
  pause: () => void;
  resume: () => void;
}

/** The part of a chunked turn still unspoken once chunk `failedIndex` failed. */
export const remainingText = (chunks: string[], failedIndex: number): string =>
  chunks.slice(Math.max(0, failedIndex)).join(' ').trim();

export interface ElliottSttCallbacks {
  /**
   * The words of the phrase being spoken right now, not yet final (browser
   * engine only: the server's transcription has no partial results).
   */
  onPartial: (text: string) => void;
  /**
   * Fires once per recording, the moment real speech is first detected.
   */
  onSpeechStart?: () => void;
  /**
   * Fires, throttled, whenever the learner is actually making speech. Drives
   * the idle guard: a mic that hears nothing for long enough closes itself,
   * so a learner who walks away does not come back to a recording of the room.
   */
  onActivity?: () => void;
  /**
   * A finished phrase, written down while the recording carries on. Phrases
   * arrive in the order they were spoken. This is what puts the learner's
   * words on screen as they talk.
   */
  onPhrase?: (text: string) => void;
  /**
   * Whether something has been heard that is not written down yet: a phrase in
   * progress, or one on its way back from transcription. Reported on change.
   */
  onPending?: (pending: boolean) => void;
  /**
   * The recording has ended and every phrase has been handed over. Carries
   * whatever was not already sent through onPhrase. Only the caller's own
   * stop() gets here: a pause never ends a recording.
   */
  onFinal: (text: string) => void;
  /** Mic permission denied / revoked — the caller should fall back to text. */
  onPermissionDenied: () => void;
  /**
   * The speech backend is unusable even though the API exists — Chromium-family
   * browsers (Brave, plain Chromium, Electron) expose webkitSpeechRecognition
   * but every start fails with 'network'. The caller should fall back to text.
   */
  onUnavailable: () => void;
}

export interface ElliottSttEngine {
  start: (cb: ElliottSttCallbacks) => void;
  /** End the recording and write down whatever is left of it. */
  stop: () => void;
  /** Discard the capture silently; no callbacks fire. */
  abort: () => void;
}

export const isVoiceConversationSupported = (): boolean =>
  Boolean(((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition) && window.speechSynthesis);

// ── Browser TTS (speechSynthesis) ────────────────────────────────────────────

// Elliott's voice must read WARM and human, never robotic (product requirement
// — this also drives the eventual ElevenLabs voice sign-off). Browsers differ
// wildly, so rank by warmth: Edge's neural voices ("… Online (Natural)") are
// the best the platform ships, then Chrome's remote Google voice, then macOS
// premium/enhanced local voices — the flat default synth voices only as a last
// resort. (Typed loosely — Web Speech globals aren't in this tsconfig's libs.)
const pickVoice = (): any => {
  const voices: any[] = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const en = voices.filter((v) => v.lang?.startsWith('en'));
  return (
    en.find((v) => /natural/i.test(v.name) && /aria|jenny|sonia|libby/i.test(v.name)) ||
    en.find((v) => /natural/i.test(v.name)) ||
    en.find((v) => v.name === 'Google US English') ||
    en.find((v) => /google/i.test(v.name)) ||
    en.find((v) => /premium|enhanced/i.test(v.name)) ||
    en.find((v) => v.name === 'Samantha') ||
    en[0] ||
    null
  );
};

// Chrome silently cuts long utterances (~15s); queueing sentence-sized chunks
// avoids the bug and keeps cancel() responsive mid-turn. Exported: the server
// engine chunks the same way so the first sentence's audio arrives fast.
export const splitIntoChunks = (text: string): string[] => {
  const sentences =
    text
      .replace(/\s+/g, ' ')
      .trim()
      .match(/[^.!?…]+[.!?…]*\s*/g) || [];
  const chunks: string[] = [];
  let current = '';
  for (const sentence of sentences) {
    if (current && current.length + sentence.length > 180) {
      chunks.push(current.trim());
      current = sentence;
    } else {
      current += sentence;
    }
  }
  if (current.trim()) chunks.push(current.trim());
  return chunks;
};

export const createBrowserTts = (): ElliottTtsEngine => {
  // Voices load asynchronously in Chrome — warm the cache so the first real
  // speak() already has the good voice available.
  try {
    window.speechSynthesis.getVoices();
  } catch {
    /* noop */
  }
  let session = 0;
  // Pause/resume for the turn in flight. They live inside speak()'s closure,
  // which owns the chunk queue and the watchdog they have to reach.
  let current: { pause: () => void; resume: () => void } | null = null;

  return {
    speak(text, cb) {
      session += 1;
      const mySession = session;
      current = null;
      window.speechSynthesis.cancel();
      const chunks = splitIntoChunks(text);
      if (!chunks.length) {
        cb.onEnd();
        return;
      }
      // Environments with a voiceless speechSynthesis (headless/stripped
      // Chromium) accept speak() but never fire utterance events — without a
      // watchdog the conversation would hang on "speaking" forever.
      let settled = false;
      let watchdog = 0;
      // Bumped by pause(), so the utterance it cancels can neither advance the
      // queue (onend) nor fail the turn (the 'interrupted' onerror).
      let generation = 0;
      let paused = false;
      const settle = (fn: () => void) => {
        if (settled || mySession !== session) return;
        settled = true;
        window.clearTimeout(watchdog);
        fn();
      };
      // Sized to what is still unsaid, so a long pause never trips it: pause()
      // disarms it and resume() re-arms it for the rest of the turn.
      const armWatchdog = (fromIndex: number) => {
        window.clearTimeout(watchdog);
        const unsaid = chunks.slice(fromIndex).join(' ');
        watchdog = window.setTimeout(() => {
          if (settled || mySession !== session) return; // superseded by a later speak/cancel
          session += 1; // invalidate pending chunk callbacks
          window.speechSynthesis.cancel();
          settled = true;
          cb.onError();
        }, 5000 + unsaid.length * 120);
      };
      armWatchdog(0);
      const voice = pickVoice();
      let index = 0;
      let started = false;
      const speakNext = () => {
        if (settled || mySession !== session || paused) return; // cancelled / timed out / held
        if (index >= chunks.length) {
          settle(cb.onEnd);
          return;
        }
        const myGeneration = generation;
        const isCurrent = () => mySession === session && myGeneration === generation;
        const chunkText = chunks[index];
        const utterance = new SpeechSynthesisUtterance(chunkText);
        index += 1;
        if (voice) utterance.voice = voice;
        // A touch below default pace — reads as calm and welcoming, not rushed.
        utterance.rate = 0.97;
        // Word-synced captions from the REAL boundary events (where the voice
        // emits them); the initial -1 shows the sentence whole as a fallback.
        const words = chunkText.split(/\s+/).filter(Boolean);
        const offsets: number[] = [];
        let pos = 0;
        for (const w of words) {
          const at = chunkText.indexOf(w, pos);
          offsets.push(at);
          pos = at + w.length;
        }
        utterance.onboundary = (e: any) => {
          if (!isCurrent() || e?.name !== 'word') return;
          const charIndex = e.charIndex ?? 0;
          let idx = 0;
          for (let i = 0; i < offsets.length; i += 1) if (charIndex >= offsets[i]) idx = i;
          cb.onCaption?.(words, idx);
        };
        utterance.onstart = () => {
          if (!isCurrent()) return;
          cb.onCaption?.(words, -1);
          if (started) return;
          started = true;
          cb.onStart?.();
        };
        utterance.onend = () => {
          if (isCurrent()) speakNext();
        };
        utterance.onerror = () => {
          // cancel() fires an 'interrupted' error on the pending utterance —
          // the session and generation checks filter that out.
          if (!isCurrent()) return;
          settle(cb.onError);
        };
        window.speechSynthesis.speak(utterance);
      };
      current = {
        pause: () => {
          if (settled || mySession !== session || paused) return;
          paused = true;
          generation += 1;
          window.clearTimeout(watchdog);
          // `index` already points past the sentence being voiced (it moves
          // when an utterance is queued), so step back to restart that one.
          index = Math.max(0, index - 1);
          window.speechSynthesis.cancel();
        },
        resume: () => {
          if (settled || mySession !== session || !paused) return;
          paused = false;
          armWatchdog(index);
          speakNext();
        }
      };
      speakNext();
    },
    cancel() {
      session += 1;
      current = null;
      window.speechSynthesis.cancel();
    },
    pause() {
      current?.pause();
    },
    resume() {
      current?.resume();
    }
  };
};

// ── Browser STT (SpeechRecognition) ──────────────────────────────────────────

export const createBrowserStt = (): ElliottSttEngine | null => {
  const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!SR) return null;

  let rec: any = null;
  let cb: ElliottSttCallbacks | null = null;
  // Every result the recognizer marks final is handed over as a phrase at
  // once, so the learner sees their words while they carry on. Chrome also
  // ends recognition on its own after a quiet spell; the recording survives
  // that by starting a fresh instance, and nothing needs carrying across.
  let interim = '';
  // Results of the live instance already handed over as phrases.
  let emitted = 0;
  // Phrases held for onFinal when the caller takes no onPhrase.
  let banked = '';
  let pending = false;
  let announcedSpeech = false;
  let deadStartTimer: number | null = null;
  let active = false;

  const clearTimer = () => {
    if (deadStartTimer !== null) {
      window.clearTimeout(deadStartTimer);
      deadStartTimer = null;
    }
  };

  const emitPhrase = (text: string) => {
    const phrase = text.trim();
    if (!phrase) return;
    if (cb?.onPhrase) cb.onPhrase(phrase);
    else banked = `${banked} ${phrase}`.trim();
  };

  const reportPending = (next: boolean) => {
    if (next === pending) return;
    pending = next;
    cb?.onPending?.(next);
  };

  const finalize = () => {
    if (!active) return;
    active = false;
    clearTimer();
    const finalCb = cb;
    const text = `${banked} ${interim}`.trim();
    reportPending(false);
    const r = rec;
    rec = null;
    cb = null;
    try {
      r?.stop();
    } catch {
      /* noop */
    }
    finalCb?.onFinal(text);
  };

  const teardown = () => {
    active = false;
    clearTimer();
    const r = rec;
    rec = null;
    cb = null;
    if (r) {
      r.onresult = null;
      r.onend = null;
      r.onerror = null;
      try {
        r.abort();
      } catch {
        /* noop */
      }
    }
  };

  return {
    start(callbacks) {
      teardown(); // never two captures at once
      cb = callbacks;
      interim = '';
      emitted = 0;
      banked = '';
      pending = false;
      announcedSpeech = false;
      active = true;
      // Chromium-family browsers expose the API but every start dies with
      // 'network' (no Google speech backend). Detect the churn — errors or
      // instant empty restarts — and bail out instead of looping forever.
      let backendFailures = 0;
      let recStartedAt = 0;
      let sawEvent = false;

      const giveUp = () => {
        const unavailable = cb;
        teardown();
        unavailable?.onUnavailable();
      };

      // Third unusable signature: start() is accepted and then NOTHING ever
      // fires — no audiostart, no error, no end (permission prompt suppressed,
      // stripped speech service). A working mic emits audiostart right away and
      // even dead silence produces a 'no-speech' error within ~8s, so a fully
      // event-less window means the capture is dead. Users slow to answer the
      // permission prompt land on the text fallback and can hit "Retry voice".
      const armDeadStartWatchdog = () => {
        if (deadStartTimer !== null) window.clearTimeout(deadStartTimer);
        deadStartTimer = window.setTimeout(() => {
          if (active && !sawEvent) giveUp();
        }, 15_000);
      };

      const startRec = () => {
        if (!active) return;
        const r = new SR();
        rec = r;
        recStartedAt = Date.now();
        emitted = 0;
        let heardHere = false;
        // ALWAYS en-US: the sim is English-only (PRD v1) and the learner
        // answers in English. navigator.language here made pt-BR/etc. browsers
        // transcribe English answers through the wrong language model —
        // "Elliott doesn't understand me" class.
        r.lang = 'en-US';
        r.continuous = true;
        r.interimResults = true;
        r.onresult = (e: any) => {
          if (!active || r !== rec) return;
          sawEvent = true;
          backendFailures = 0; // the backend clearly works
          const { results } = e;
          while (emitted < results.length && results[emitted].isFinal) {
            heardHere = true;
            emitPhrase(String(results[emitted][0]?.transcript || ''));
            emitted += 1;
          }
          let text = '';
          for (let i = emitted; i < results.length; i += 1) text += results[i][0]?.transcript || '';
          interim = text.trim();
          if (interim) heardHere = true;
          // Results only arrive while words are being spoken.
          cb?.onActivity?.();
          if (!announcedSpeech && heardHere) {
            announcedSpeech = true;
            cb?.onSpeechStart?.();
          }
          reportPending(Boolean(interim));
          cb?.onPartial(interim);
        };
        r.onaudiostart = () => {
          sawEvent = true; // the capture is genuinely live
        };
        r.onerror = (e: any) => {
          if (!active || r !== rec) return;
          sawEvent = true;
          const code = e?.error;
          if (code === 'not-allowed' || code === 'service-not-allowed') {
            const denied = cb;
            teardown();
            denied?.onPermissionDenied();
            return;
          }
          if (code === 'network' || code === 'audio-capture') {
            backendFailures += 1;
            if (backendFailures >= 3) giveUp();
          }
          // 'no-speech' / 'aborted' → onend follows and handles it.
        };
        r.onend = () => {
          if (!active || r !== rec) return;
          sawEvent = true;
          // Chrome's own quiet-spell cutoff is NOT the end of the recording;
          // only the caller's stop() is. Hand over anything left unfinished
          // and reopen the capture.
          if (interim) {
            emitPhrase(interim);
            interim = '';
            reportPending(false);
            cb?.onPartial('');
          } else if (!heardHere && Date.now() - recStartedAt < 1000) {
            // An instant empty end is another unusable-backend signature.
            backendFailures += 1;
            if (backendFailures >= 3) {
              giveUp();
              return;
            }
          }
          startRec();
        };
        try {
          r.start();
        } catch {
          /* start() throws if a capture is already running — teardown() above prevents it */
        }
        armDeadStartWatchdog();
      };
      startRec();
    },
    stop: finalize,
    abort: teardown
  };
};
